import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
  ledgerSwitchSx,
  fieldLabelSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  PageHeader,
  LedgerCard,
  SectionHeader,
  ActionIcon,
} from '../../components/admin/LedgerPrimitives';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // General
  const [siteName, setSiteName] = useState('Almaship - Imperia Tech LLC');
  const [siteDescription, setSiteDescription] = useState('International shipping and logistics platform');
  const [supportEmail, setSupportEmail] = useState('support@almaship.co');
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Email
  const [smtpHost, setSmtpHost] = useState('smtp.almaship.co');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('notifications@almaship.co');
  const [smtpPassword, setSmtpPassword] = useState('********');
  const [emailFrom, setEmailFrom] = useState('no-reply@almaship.co');
  const [emailFromName, setEmailFromName] = useState('Almaship - Imperia Tech LLC');

  // Templates
  const [templates] = useState([
    { id: 1, name: 'Welcome email', subject: 'Welcome to Almaship' },
    { id: 2, name: 'Order confirmation', subject: 'Your order details' },
    { id: 3, name: 'Password reset', subject: 'Reset your password' },
    { id: 4, name: 'Shipping confirmation', subject: 'Your package is on its way' },
  ]);

  // Payment
  const [currencyCode, setCurrencyCode] = useState('ILS');
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [paypalEnabled, setPaypalEnabled] = useState(true);
  const [stripePublicKey, setStripePublicKey] = useState('pk_test_***********************');
  const [stripeSecretKey, setStripeSecretKey] = useState('sk_test_***********************');
  const [paypalClientId, setPaypalClientId] = useState('client_id_***********************');

  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const SAVE_ROW = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1.5 }}>
      <Button startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
        Reset
      </Button>
      <Button onClick={handleSave} startIcon={<SaveIcon sx={{ fontSize: 16 }} />} sx={primaryBtnSx}>
        Save changes
      </Button>
    </Box>
  );

  const ToggleRow: React.FC<{ label: string; checked?: boolean; onChange?: (v: boolean) => void; defaultChecked?: boolean }> = ({
    label, checked, onChange, defaultChecked,
  }) => (
    <Box
      sx={{
        border: `1px solid ${INK.hairlineSoft}`,
        borderRadius: 0.5,
        px: 1.5,
        py: 1.1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.text }}>{label}</Typography>
      <Switch
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={onChange ? (e) => onChange(e.target.checked) : undefined}
        size="small"
        sx={ledgerSwitchSx}
      />
    </Box>
  );

  return (
    <LedgerPage eyebrowFooter="Almaship · Configuration">
      <PageHeader
        eyebrow="Admin · Config"
        title={
          <>
            Settings
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              .
            </Box>
          </>
        }
        description="Site, email, payment, and security configuration for the platform."
      />

      {saveSuccess && (
        <Alert
          severity="success"
          sx={{ borderRadius: 0, mb: 3, fontFamily: FONTS.sans }}
          action={
            <IconButton size="small" onClick={() => setSaveSuccess(false)}>
              <CloseIcon sx={{ fontSize: 16, color: 'inherit' }} />
            </IconButton>
          }
        >
          Settings saved successfully.
        </Alert>
      )}

      <Box sx={{ borderBottom: `1px solid ${INK.hairline}`, mb: 1 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTabs-indicator': { bgcolor: INK.accent, height: 2 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontFamily: FONTS.sans,
              fontWeight: 500,
              fontSize: 13.5,
              color: INK.subtle,
              minHeight: 44,
              minWidth: 100,
              '&.Mui-selected': { color: INK.text, fontWeight: 600 },
            },
          }}
        >
          <Tab label="General" />
          <Tab label="Email" />
          <Tab label="Notifications" />
          <Tab label="Payment" />
          <Tab label="Security" />
        </Tabs>
      </Box>

      {/* General */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <LedgerCard>
            <SectionHeader number="01" eyebrow="Site" title="Site information" />
            <Stack spacing={2}>
              <Box>
                <Typography sx={fieldLabelSx}>Site name</Typography>
                <TextField fullWidth size="small" value={siteName} onChange={(e) => setSiteName(e.target.value)} sx={ledgerInputSx} />
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Description</Typography>
                <TextField fullWidth size="small" multiline rows={2} value={siteDescription} onChange={(e) => setSiteDescription(e.target.value)} sx={ledgerInputSx} />
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Support email</Typography>
                <TextField fullWidth size="small" type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} sx={ledgerInputSx} />
              </Box>
              <ToggleRow label="Maintenance mode" checked={maintenanceMode} onChange={setMaintenanceMode} />
            </Stack>
          </LedgerCard>

          <LedgerCard>
            <SectionHeader number="02" eyebrow="Locale" title="Localization" />
            <Stack spacing={2}>
              <Box>
                <Typography sx={fieldLabelSx}>Default language</Typography>
                <FormControl fullWidth size="small">
                  <Select defaultValue="en" sx={ledgerSelectSx}>
                    {[
                      ['en', 'English'], ['es', 'Spanish'], ['fr', 'French'], ['de', 'German'], ['zh', 'Chinese'],
                    ].map(([v, l]) => (
                      <MenuItem key={v} value={v} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Timezone</Typography>
                <FormControl fullWidth size="small">
                  <Select defaultValue="UTC" sx={ledgerSelectSx}>
                    {['UTC', 'EST', 'CST', 'MST', 'PST'].map((tz) => (
                      <MenuItem key={tz} value={tz} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>{tz}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Date format</Typography>
                <FormControl fullWidth size="small">
                  <Select defaultValue="YYYY-MM-DD" sx={ledgerSelectSx}>
                    {['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'].map((f) => (
                      <MenuItem key={f} value={f} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>{f}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Stack>
          </LedgerCard>
        </Box>
        {SAVE_ROW}
      </TabPanel>

      {/* Email */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <LedgerCard>
            <SectionHeader number="01" eyebrow="SMTP" title="Mail server" />
            <Stack spacing={2}>
              {[
                ['SMTP host', smtpHost, setSmtpHost, 'text'],
                ['SMTP port', smtpPort, setSmtpPort, 'text'],
                ['SMTP username', smtpUser, setSmtpUser, 'text'],
              ].map(([label, value, setter, type]: any) => (
                <Box key={label}>
                  <Typography sx={fieldLabelSx}>{label}</Typography>
                  <TextField fullWidth size="small" type={type} value={value} onChange={(e) => setter(e.target.value)} sx={ledgerInputSx} />
                </Box>
              ))}
              <Box>
                <Typography sx={fieldLabelSx}>SMTP password</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={showPassword ? 'text' : 'password'}
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)} sx={{ color: INK.muted }}>
                          {showPassword ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={ledgerInputSx}
                />
              </Box>
            </Stack>
          </LedgerCard>

          <LedgerCard>
            <SectionHeader number="02" eyebrow="From" title="Sender identity" />
            <Stack spacing={2}>
              <Box>
                <Typography sx={fieldLabelSx}>From email</Typography>
                <TextField fullWidth size="small" type="email" value={emailFrom} onChange={(e) => setEmailFrom(e.target.value)} sx={ledgerInputSx} />
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>From name</Typography>
                <TextField fullWidth size="small" value={emailFromName} onChange={(e) => setEmailFromName(e.target.value)} sx={ledgerInputSx} />
              </Box>
              <ToggleRow label="Enable email notifications" defaultChecked />
              <Button fullWidth sx={{ ...ghostBtnSx, py: 1.2 }}>
                Test email configuration
              </Button>
            </Stack>
          </LedgerCard>
        </Box>
        {SAVE_ROW}
      </TabPanel>

      {/* Notifications */}
      <TabPanel value={tabValue} index={2}>
        <Stack spacing={2}>
          <LedgerCard padded={false}>
            <Box sx={{ p: 3, borderBottom: `1px solid ${INK.hairline}` }}>
              <SectionHeader
                number="01"
                eyebrow="Templates"
                title="Notification templates"
                action={<Button startIcon={<AddIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>Add</Button>}
              />
            </Box>
            <List dense disablePadding>
              {templates.map((t, i) => (
                <ListItem
                  key={t.id}
                  sx={{
                    px: 3,
                    py: 1.75,
                    borderBottom: i === templates.length - 1 ? 'none' : `1px solid ${INK.hairlineSoft}`,
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <ActionIcon title="Edit" onClick={() => {}}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </ActionIcon>
                      <ActionIcon title="Delete" onClick={() => {}} danger>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </ActionIcon>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={<Typography sx={{ fontFamily: FONTS.sans, fontSize: 13.5, fontWeight: 500, color: INK.text }}>{t.name}</Typography>}
                    secondary={<Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle }}>{t.subject}</Typography>}
                  />
                </ListItem>
              ))}
            </List>
          </LedgerCard>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <LedgerCard>
              <SectionHeader number="02" eyebrow="User" title="Email notifications" />
              <Stack spacing={1}>
                {['Order confirmation', 'Shipping confirmation', 'Delivery confirmation', 'Password reset', 'Account creation'].map((l) => (
                  <ToggleRow key={l} label={l} defaultChecked />
                ))}
              </Stack>
            </LedgerCard>
            <LedgerCard>
              <SectionHeader number="03" eyebrow="Admin" title="Internal alerts" />
              <Stack spacing={1}>
                {['New user registration', 'New order placed', 'Order cancelled', 'Payment failed', 'Contact form submission'].map((l) => (
                  <ToggleRow key={l} label={l} defaultChecked />
                ))}
              </Stack>
            </LedgerCard>
          </Box>
        </Stack>
        {SAVE_ROW}
      </TabPanel>

      {/* Payment */}
      <TabPanel value={tabValue} index={3}>
        <Stack spacing={2}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <LedgerCard>
              <SectionHeader number="01" eyebrow="Currency" title="General payment" />
              <Stack spacing={2}>
                <Box>
                  <Typography sx={fieldLabelSx}>Default currency</Typography>
                  <FormControl fullWidth size="small">
                    <Select value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)} sx={ledgerSelectSx}>
                      <MenuItem value="ILS" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Israeli New Shekel (ILS)</MenuItem>
                      <MenuItem value="USD" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>US Dollar (USD)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <ToggleRow label="Show currency symbol" defaultChecked />
                <ToggleRow label="Enable tax calculations" defaultChecked />
              </Stack>
            </LedgerCard>
            <LedgerCard>
              <SectionHeader number="02" eyebrow="Methods" title="Payment methods" />
              <Stack spacing={1}>
                <ToggleRow label="Stripe" checked={stripeEnabled} onChange={setStripeEnabled} />
                <ToggleRow label="PayPal" checked={paypalEnabled} onChange={setPaypalEnabled} />
                <ToggleRow label="Bank transfer" />
                <ToggleRow label="Cash on delivery" />
              </Stack>
            </LedgerCard>
          </Box>

          {stripeEnabled && (
            <LedgerCard>
              <SectionHeader number="03" eyebrow="Stripe" title="Stripe configuration" />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography sx={fieldLabelSx}>Public key</Typography>
                  <TextField fullWidth size="small" value={stripePublicKey} onChange={(e) => setStripePublicKey(e.target.value)} sx={ledgerInputSx} />
                </Box>
                <Box>
                  <Typography sx={fieldLabelSx}>Secret key</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type={showPassword ? 'text' : 'password'}
                    value={stripeSecretKey}
                    onChange={(e) => setStripeSecretKey(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPassword(!showPassword)} sx={{ color: INK.muted }}>
                            {showPassword ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={ledgerInputSx}
                  />
                </Box>
                <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                  <ToggleRow label="Test mode" defaultChecked />
                </Box>
              </Box>
            </LedgerCard>
          )}

          {paypalEnabled && (
            <LedgerCard>
              <SectionHeader number="04" eyebrow="PayPal" title="PayPal configuration" />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography sx={fieldLabelSx}>Client ID</Typography>
                  <TextField fullWidth size="small" value={paypalClientId} onChange={(e) => setPaypalClientId(e.target.value)} sx={ledgerInputSx} />
                </Box>
                <Box>
                  <Typography sx={fieldLabelSx}>Secret</Typography>
                  <TextField fullWidth size="small" type="password" value="************" sx={ledgerInputSx} />
                </Box>
                <Box sx={{ gridColumn: { md: '1 / -1' } }}>
                  <ToggleRow label="Sandbox mode" defaultChecked />
                </Box>
              </Box>
            </LedgerCard>
          )}
        </Stack>
        {SAVE_ROW}
      </TabPanel>

      {/* Security */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <LedgerCard>
            <SectionHeader number="01" eyebrow="Access" title="Security settings" />
            <Stack spacing={2}>
              <ToggleRow label="Force SSL" defaultChecked />
              <ToggleRow label="Enable two-factor authentication" defaultChecked />
              <Box>
                <Typography sx={fieldLabelSx}>Session timeout</Typography>
                <FormControl fullWidth size="small">
                  <Select defaultValue="30" sx={ledgerSelectSx}>
                    {[
                      ['15', '15 minutes'], ['30', '30 minutes'], ['60', '1 hour'], ['120', '2 hours'], ['240', '4 hours'],
                    ].map(([v, l]) => (
                      <MenuItem key={v} value={v} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography sx={fieldLabelSx}>Max login attempts</Typography>
                <TextField fullWidth size="small" type="number" defaultValue="5" sx={ledgerInputSx} />
              </Box>
            </Stack>
          </LedgerCard>

          <LedgerCard>
            <SectionHeader number="02" eyebrow="Password" title="Password policy" />
            <Stack spacing={2}>
              <Box>
                <Typography sx={fieldLabelSx}>Minimum length</Typography>
                <TextField fullWidth size="small" type="number" defaultValue="8" sx={ledgerInputSx} />
              </Box>
              <ToggleRow label="Require uppercase letters" defaultChecked />
              <ToggleRow label="Require numbers" defaultChecked />
              <ToggleRow label="Require special characters" defaultChecked />
              <Box>
                <Typography sx={fieldLabelSx}>Password expiry (days)</Typography>
                <TextField fullWidth size="small" type="number" defaultValue="90" sx={ledgerInputSx} />
              </Box>
            </Stack>
          </LedgerCard>
        </Box>
        {SAVE_ROW}
      </TabPanel>

      <Box sx={{ display: 'none' }}>
        <FormControlLabel control={<Switch />} label="" />
        <Divider />
      </Box>
    </LedgerPage>
  );
};

export default SettingsPage;
